import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { MessageCircle, X, Send } from 'lucide-react';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [chatId, setChatId] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [userName, setUserName] = useState('');
  const [showNameInput, setShowNameInput] = useState(true);
  const [adminTyping, setAdminTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const typingStopRef = useRef(null);
  const adminTypingTimerRef = useRef(null);
  
  // Use environment variable for backend URL with fallback to current origin
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!isOpen) return;

    const newSocket = io(backendUrl, {
      transports: ['polling', 'websocket'],
      path: '/api/socket.io'
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
      
      // Genera o recupera chat_id
      let storedChatId = localStorage.getItem('tp_chat_id');
      if (!storedChatId) {
        storedChatId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('tp_chat_id', storedChatId);
      }
      
      setChatId(storedChatId);
      
      // Join chat room
      newSocket.emit('join_chat', {
        chat_id: storedChatId,
        user_type: 'user'
      });
    });

    newSocket.on('chat_history', (data) => {
      setMessages(data.messages || []);
    });

    newSocket.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
      // Se arriva msg da admin/bot, nascondi indicator
      if (message.sender_type !== 'user') setAdminTyping(false);
    });

    newSocket.on('typing_indicator', (data) => {
      if (data?.by === 'admin' && data?.typing) {
        setAdminTyping(true);
        clearTimeout(adminTypingTimerRef.current);
        adminTypingTimerRef.current = setTimeout(() => setAdminTyping(false), 4000);
      } else if (data?.by === 'admin' && data?.typing === false) {
        setAdminTyping(false);
      }
    });

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [isOpen, backendUrl]);

  const handleStartChat = () => {
    if (userName.trim()) {
      setShowNameInput(false);
      localStorage.setItem('tp_chat_user_name', userName);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !socket || !chatId) return;

    const savedName = localStorage.getItem('tp_chat_user_name') || userName || 'Utente';

    socket.emit('send_message', {
      chat_id: chatId,
      message: inputMessage,
      sender_type: 'user',
      sender_name: savedName
    });
    // Stop typing
    socket.emit('stop_typing', { chat_id: chatId, by: 'user' });
    clearTimeout(typingStopRef.current);

    setInputMessage('');
  };

  const handleInputChange = (e) => {
    setInputMessage(e.target.value);
    if (socket && chatId) {
      socket.emit('user_typing', { chat_id: chatId });
      clearTimeout(typingStopRef.current);
      typingStopRef.current = setTimeout(() => {
        socket.emit('stop_typing', { chat_id: chatId, by: 'user' });
      }, 2500);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    
    // Recupera nome salvato
    const savedName = localStorage.getItem('tp_chat_user_name');
    if (savedName) {
      setUserName(savedName);
      setShowNameInput(false);
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={toggleChat}
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-16 h-16 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110"
          style={{ fontFamily: 'Oswald, sans-serif' }}
          aria-label="Apri chat"
        >
          <MessageCircle size={28} />
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-6 right-6 z-50 w-[90vw] sm:w-96 h-[600px] max-h-[80vh] rounded-lg shadow-2xl flex flex-col"
          style={{ backgroundColor: 'var(--tp-dark)', border: '2px solid var(--tp-red)' }}>
          
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-700"
            style={{ backgroundColor: 'var(--tp-black)' }}>
            <div className="flex items-center gap-2">
              <MessageCircle size={20} className="text-red-500" />
              <div>
                <h3 className="text-white font-bold text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  CHAT TECNICHE PERFETTE
                </h3>
                <p className="text-xs text-gray-400">
                  {isConnected ? '🟢 Online' : '🔴 Offline'} • 9 AM - 6 PM
                </p>
              </div>
            </div>
            <button
              onClick={toggleChat}
              className="text-gray-400 hover:text-white transition-colors"
              aria-label="Chiudi chat"
            >
              <X size={20} />
            </button>
          </div>

          {/* Name Input Screen */}
          {showNameInput ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <div className="text-center mb-6">
                <h4 className="text-white text-xl font-bold mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  BENVENUTO!
                </h4>
                <p className="text-gray-400 text-sm">
                  Inserisci il tuo nome per iniziare la chat
                </p>
              </div>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleStartChat()}
                placeholder="Il tuo nome..."
                className="w-full p-3 rounded bg-gray-800 border border-gray-600 text-white focus:border-red-500 focus:outline-none mb-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
                autoFocus
              />
              <button
                onClick={handleStartChat}
                disabled={!userName.trim()}
                className="w-full px-6 py-3 rounded bg-red-600 hover:bg-red-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                INIZIA CHAT
              </button>
            </div>
          ) : (
            <>
              {/* Messages Container */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center text-gray-500 text-sm mt-8">
                    <p>Ciao {userName}! 👋</p>
                    <p className="mt-2">Come possiamo aiutarti?</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] rounded-lg p-3 ${
                          msg.sender_type === 'user'
                            ? 'bg-red-600 text-white'
                            : msg.sender_type === 'bot'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 text-white'
                        }`}
                      >
                        <p className="text-xs font-bold mb-1" style={{ fontFamily: 'Oswald, sans-serif' }}>
                          {msg.sender_name}
                        </p>
                        <p className="text-sm break-words">{msg.message}</p>
                        <p className="text-xs opacity-70 mt-1">
                          {new Date(msg.timestamp).toLocaleTimeString('it-IT', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))
                )}
                {adminTyping && (
                  <div className="flex justify-start" data-testid="chat-typing-indicator">
                    <div className="bg-gray-700 text-white rounded-lg p-3 flex items-center gap-1">
                      <span className="typing-dot" />
                      <span className="typing-dot typing-dot-2" />
                      <span className="typing-dot typing-dot-3" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Form */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-700">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={inputMessage}
                    onChange={handleInputChange}
                    placeholder="Scrivi un messaggio..."
                    className="flex-1 p-3 rounded bg-gray-800 border border-gray-600 text-white placeholder:text-gray-500 focus:border-red-500 focus:outline-none text-sm"
                    disabled={!isConnected}
                  />
                  <button
                    type="submit"
                    disabled={!inputMessage.trim() || !isConnected}
                    className="px-4 py-3 rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="Invia messaggio"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default ChatWidget;
